import React from 'react';

export default function ParticipantCard({ participant }) {
  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'disconnected': 'warning',
      'flagged': 'danger',
      'submitted': 'info'
    };
    return colors[status] || 'secondary';
  };

  const getViolationSeverity = (violationCount) => {
    if (violationCount >= 5) return 'danger';
    if (violationCount >= 3) return 'warning';
    return 'success';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleTimeString();
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const then = new Date(timestamp);
    const diffInMinutes = Math.floor((now - then) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours}h ${diffInMinutes % 60}m ago`;
  };

  return (
    <div className="card participant-card h-100">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="flex-grow-1">
            <h6 className="mb-1 text-truncate" title={participant.user_name}>
              {participant.user_name || 'Unknown User'}
            </h6>
            <small className="text-muted">
              ID: {participant.user_id}
            </small>
          </div>
          <span className={`badge bg-${getStatusColor(participant.status)} ms-2`}>
            {participant.status?.toUpperCase() || 'UNKNOWN'}
          </span>
        </div>

        <div className="participant-stats">
          <div className="row text-center">
            <div className="col-4">
              <div className="stat-item">
                <div className="stat-value text-primary">
                  {participant.answers_submitted || 0}
                </div>
                <div className="stat-label">Answers</div>
              </div>
            </div>
            <div className="col-4">
              <div className="stat-item">
                <div className={`stat-value text-${getViolationSeverity(participant.violations || 0)}`}>
                  {participant.violations || 0}
                </div>
                <div className="stat-label">Violations</div>
              </div>
            </div>
            <div className="col-4">
              <div className="stat-item">
                <div className="stat-value text-info">
                  {formatTime(participant.joined_at)}
                </div>
                <div className="stat-label">Joined</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <small className="text-muted">
            <i className="fas fa-clock me-1"></i>
            Last activity: {getTimeSince(participant.last_activity)}
          </small>
        </div>

        {participant.status === 'flagged' && (
          <div className="mt-2">
            <span className="badge bg-danger w-100">
              <i className="fas fa-flag me-1"></i>
              HIGH VIOLATION COUNT
            </span>
          </div>
        )}

        {participant.status === 'submitted' && participant.submission_time && (
          <div className="mt-2">
            <small className="text-success">
              <i className="fas fa-check-circle me-1"></i>
              Submitted at {formatTime(participant.submission_time)}
            </small>
          </div>
        )}

        {participant.status === 'disconnected' && (
          <div className="mt-2">
            <small className="text-warning">
              <i className="fas fa-wifi me-1"></i>
              Disconnected {getTimeSince(participant.disconnected_at)}
            </small>
          </div>
        )}
      </div>

      <style jsx>{`
        .participant-card {
          border-left: 4px solid;
          border-left-color: ${participant.status === 'active' ? '#28a745' :
                             participant.status === 'flagged' ? '#dc3545' :
                             participant.status === 'submitted' ? '#17a2b8' :
                             participant.status === 'disconnected' ? '#ffc107' : '#6c757d'};
          transition: all 0.3s ease;
        }
        
        .participant-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        
        .stat-item {
          padding: 4px 0;
        }
        
        .stat-value {
          font-size: 16px;
          font-weight: 600;
          line-height: 1;
        }
        
        .stat-label {
          font-size: 10px;
          text-transform: uppercase;
          color: #6c757d;
          letter-spacing: 0.5px;
        }
      `}</style>
    </div>
  );
}
